import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import {
  LeaderboardGroup,
  LeaderboardPeriod,
  LeaderboardRecordsResponse,
  LeaderboardResponse,
  LeaderboardQueryParams,
  LeaderboardStandingResponse,
} from 'types/interfaces/leaderboard';

export const leaderboardApi = createApi({
  reducerPath: 'leaderboardApi',
  baseQuery: baseQuery,
  tagTypes: ['Leaderboard'],
  endpoints: (builder) => ({
    // Get leaderboard data (public endpoint)
    getLeaderboard: builder.query<LeaderboardResponse, LeaderboardQueryParams>({
      query: ({ category, period, country, search, page = 1, limit = 25, minWantListCount }) => {
        const params = new URLSearchParams();
        params.append('category', category);
        params.append('period', period);
        params.append('page', String(page));
        params.append('limit', String(limit));
        if (country) params.append('country', country);
        if (search) params.append('search', search);
        if (minWantListCount !== undefined) params.append('minWantListCount', String(minWantListCount));

        return {
          url: `/leaderboard?${params.toString()}`,
          method: 'GET',
          requiresAuth: false,
        };
      },
      providesTags: (_result, _error, { category, period }) => [
        { type: 'Leaderboard', id: `${category}-${period}` },
      ],
    }),

    // The viewer's rank on every board in a group (auth required; skip for
    // guests). Provides 'Leaderboard' so the points / trader-balance socket
    // invalidations refresh it along with the table.
    getLeaderboardStanding: builder.query<
      LeaderboardStandingResponse,
      { group: LeaderboardGroup; period: LeaderboardPeriod }
    >({
      query: ({ group, period }) => ({
        url: `/leaderboard/standing?group=${group}&period=${period}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_result, _error, { group, period }) => [
        { type: 'Leaderboard', id: `standing-${group}-${period}` },
      ],
    }),

    // All-time #1 on four boards (public).
    getLeaderboardRecords: builder.query<LeaderboardRecordsResponse, void>({
      query: () => ({
        url: '/leaderboard/records',
        method: 'GET',
        requiresAuth: false,
      }),
      providesTags: [{ type: 'Leaderboard', id: 'records' }],
    }),
  }),
});

export const {
  useGetLeaderboardQuery,
  useGetLeaderboardStandingQuery,
  useGetLeaderboardRecordsQuery,
} = leaderboardApi;
